import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const STAGES = [
  { v: "prospect", label: "Prospect", accent: false },
  { v: "negotiating", label: "Negotiating", accent: false },
  { v: "signed", label: "Signed", accent: false },
  { v: "live", label: "Live", accent: false },
  { v: "paid", label: "Paid", accent: true },
];

export default function Pipeline() {
  const [deals, setDeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ brand: "", stage: "prospect", value: "", deliverables: "", notes: "" });

  const load = () => api.get("/deals").then((r) => setDeals(r.data));
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/deals", { ...form, value: Number(form.value || 0) });
      setForm({ brand: "", stage: "prospect", value: "", deliverables: "", notes: "" });
      setOpen(false);
      toast.success("Deal added.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const move = async (deal, stage) => {
    await api.patch(`/deals/${deal.id}`, { stage });
    load();
  };

  const remove = async (deal) => {
    await api.delete(`/deals/${deal.id}`);
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex flex-wrap items-end justify-between mb-10 gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-2">// pipeline</p>
          <h1 className="font-display font-black text-5xl tracking-tighter">Deal pipeline.</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold" data-testid="pipeline-add-btn">
              <Plus className="w-4 h-4" /> Add deal
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141414] border-white/10">
            <DialogHeader>
              <DialogTitle className="font-display font-black text-2xl">New deal</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-3" data-testid="pipeline-new-form">
              <Input placeholder="Brand name" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
                required data-testid="pipeline-brand-input" className="bg-[#0e0e0e] border-white/10" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger className="bg-[#0e0e0e] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => (
                      <SelectItem key={s.v} value={s.v}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input type="number" placeholder="$ Value" value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  className="bg-[#0e0e0e] border-white/10 font-mono" data-testid="pipeline-value-input" />
              </div>
              <Input placeholder="Deliverables (e.g. 1 Reel + 3 Stories)" value={form.deliverables}
                onChange={(e) => setForm({ ...form, deliverables: e.target.value })}
                className="bg-[#0e0e0e] border-white/10" />
              <Textarea rows={3} placeholder="Notes" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-[#0e0e0e] border-white/10" />
              <Button type="submit" className="w-full rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold" data-testid="pipeline-create-btn">
                Add deal
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-5 gap-3">
        {STAGES.map((s) => {
          const cards = deals.filter((d) => d.stage === s.v);
          const total = cards.reduce((a, d) => a + (d.value || 0), 0);
          return (
            <div key={s.v} className="rounded-2xl bg-[#141414] border border-white/5 p-4 min-h-[400px]" data-testid={`pipeline-col-${s.v}`}>
              <div className="flex items-center justify-between mb-4">
                <p className="font-mono text-xs uppercase tracking-widest text-neutral-400">{s.label}</p>
                <span className="font-mono text-[10px] text-neutral-500">
                  {cards.length} · ${total.toLocaleString()}
                </span>
              </div>
              <div className="space-y-2">
                {cards.map((d) => (
                  <div key={d.id} className="p-3 rounded-lg bg-[#0e0e0e] border border-white/10 group" data-testid={`pipeline-card-${d.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-display font-bold">{d.brand}</p>
                      <button
                        onClick={() => remove(d)}
                        className="opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity"
                        data-testid={`pipeline-delete-${d.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    {d.value > 0 && (
                      <p className="font-mono text-xs text-[#CCFF00] mt-1">${d.value.toLocaleString()}</p>
                    )}
                    {d.deliverables && <p className="text-xs text-neutral-400 mt-1">{d.deliverables}</p>}
                    <Select value={d.stage} onValueChange={(v) => move(d, v)}>
                      <SelectTrigger className="h-7 mt-2 bg-transparent border-white/10 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STAGES.map((st) => (
                          <SelectItem key={st.v} value={st.v}>
                            {st.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
